import { CircleDot, Circle, Plane, Clock } from "lucide-react";
import { formatTime, formatDuration } from "@/components/trips/itinerary/itinerary-utils";
import { getAirportInfo } from "@/lib/airports";
import type { PlanFlightSegment } from "@/lib/types";

/* Real per-segment vertical timeline, rendered ONLY when the backend supplies
   `flight.segments`. Each segment shows depart->arrive (time + full airport
   name), the leg's aircraft, and a layover row between segments. Nothing here
   is fabricated — every field is guarded and omitted when absent. */

interface SegmentTimelineProps {
  segments: PlanFlightSegment[];
}

/** Full airport name + code from a city hint (preferred) or the IATA lookup. */
function airportLabel(
  code: string | null | undefined,
  cityHint: string | null | undefined,
): { name: string; code: string } {
  const raw = (code || "").trim().toUpperCase();
  const city = (cityHint || "").trim();
  if (city) return { name: city, code: raw };
  if (!raw) return { name: "Airport not available", code: "" };
  return { name: getAirportInfo(raw).name, code: raw };
}

/** "DL2768" -> "DL 2768" for display. Returns null for absent values and for
    anything that is not a real flight number (synthetic placeholders always
    contain a dash; real flight numbers never do), so a leg with no captured
    identity shows nothing rather than a fabricated one. */
export function formatLegFlightNumber(
  flightNumber: string | null | undefined,
): string | null {
  if (!flightNumber) return null;
  const s = flightNumber.trim().toUpperCase();
  const m = s.match(/^([A-Z][A-Z0-9]|[0-9][A-Z])\s?(\d{1,4}[A-Z]?)$/);
  return m ? `${m[1]} ${m[2]}` : null;
}

/** True when arriveLocal time is earlier than departLocal — implies +1 day. */
function arrivesNextDay(
  departLocal: string | null | undefined,
  arriveLocal: string | null | undefined,
): boolean {
  if (!departLocal || !arriveLocal) return false;
  const dep = /^(\d{1,2}):(\d{2})/.exec(departLocal.trim());
  const arr = /^(\d{1,2}):(\d{2})/.exec(arriveLocal.trim());
  if (!dep || !arr) return false;
  const depM = Number(dep[1]) * 60 + Number(dep[2]);
  const arrM = Number(arr[1]) * 60 + Number(arr[2]);
  return arrM < depM;
}

export default function SegmentTimeline({ segments }: SegmentTimelineProps) {
  const ordered = [...segments].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  return (
    <ol className="relative space-y-1 pl-1">
      {ordered.map((seg, i) => {
        const isLast = i === ordered.length - 1;
        const dep = airportLabel(seg.origin, seg.originCity);
        const arr = airportLabel(seg.destination, seg.destinationCity);
        const departT = formatTime(seg.departLocal) || "—";
        const arriveT = formatTime(seg.arriveLocal) || "—";
        const nextDay = arrivesNextDay(seg.departLocal, seg.arriveLocal);
        const aircraft = seg.aircraft && seg.aircraft.trim() ? seg.aircraft.trim() : null;
        // Real per-leg flight number ("DL2768" → "DL 2768"); omitted entirely
        // when the detail pane didn't name it. Never a synthetic placeholder —
        // those always carry a dash, real flight numbers never do.
        const legNumber = formatLegFlightNumber(seg.flightNumber);
        const layover = !isLast && seg.layoverMinutes != null
          ? formatDuration(seg.layoverMinutes)
          : null;
        const layoverWhere = (seg.layoverCity || "").trim()
          || getAirportInfo(seg.layoverAirport).name
          || (seg.layoverAirport || "").trim().toUpperCase();

        return (
          <li key={seg.position ?? i} className="space-y-1">
            {/* Segment depart */}
            <div className="flex gap-2.5">
              <span className="mt-0.5 flex flex-col items-center" aria-hidden="true">
                <CircleDot className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                <span className="my-0.5 h-5 w-px bg-border" />
              </span>
              <div className="min-w-0">
                <div className="font-mono tabular-nums font-semibold text-foreground">{departT}</div>
                <div className="truncate text-muted-foreground">
                  {dep.name}
                  {dep.code && <span className="ml-1 font-mono tabular-nums">({dep.code})</span>}
                </div>
              </div>
            </div>

            {/* Segment aircraft / leg line */}
            <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
              <span className="flex w-3.5 justify-center" aria-hidden="true">
                <Plane className="h-3.5 w-3.5 -rotate-45" />
              </span>
              <span>
                {legNumber && (
                  <span className="font-mono font-medium tabular-nums text-foreground">{legNumber}</span>
                )}
                {legNumber && <span className="mx-1.5">·</span>}
                {aircraft || "Aircraft not available"}
              </span>
            </div>

            {/* Segment arrive */}
            <div className="flex gap-2.5">
              <span className="mt-0.5 flex flex-col items-center" aria-hidden="true">
                <Circle className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                {!isLast && <span className="my-0.5 h-5 w-px bg-border" />}
              </span>
              <div className="min-w-0">
                <div className="font-mono tabular-nums font-semibold text-foreground">
                  {arriveT}
                  {arriveT !== "—" && nextDay && (
                    <span className="ml-1 text-xs font-medium text-indigo-600">+1</span>
                  )}
                </div>
                <div className="truncate text-muted-foreground">
                  {arr.name}
                  {arr.code && <span className="ml-1 font-mono tabular-nums">({arr.code})</span>}
                </div>
              </div>
            </div>

            {/* Layover row (after this segment, before the next) */}
            {!isLast && layover && (
              <div className="ml-6 flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="tabular-nums">
                  {layover} layover{layoverWhere ? " in " + layoverWhere : ""}
                </span>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
